import { AxiosInstance } from 'axios'
import { ListInput } from './types/input'
import { Disease } from './disease'
import { ListOutput } from './types/output'
import { IconSlug } from '@/v2/components/ui/icon/iconMap'

export type GuidedAttendanceForm = {
  id: number
  name: string
  slug: string
  isPublished: boolean
  isArchived: boolean
  wasAlreadyPublished: boolean
  createdAt: string
  disease: Disease
  steps: GuidedAttendanceForm.Step[]
  treatmentRuleResolvers: {
    arguments: Record<string, { key: string; source: string }>
    treatmentRuleId: number
  }[]
}

export namespace GuidedAttendanceForm {
  export enum QuestionTypeCategory {
    BASIC = 'BASIC',
    COMPOSITE = 'COMPOSITE',
    NOT_A_QUESTION = 'NOT_A_QUESTION',
  }

  export type QuestionType = {
    id: number
    slug: string
    name: string
    category: QuestionTypeCategory
    settings: {
      [key: string]: unknown
      deprecated?: boolean
      factory?: boolean
      icon?: IconSlug
      initialValue?: unknown
    }
  }

  export type Question = {
    id: number
    slug: string

    data: {
      [key: string]: unknown
      label?: string
      prompt?: string
      title?: string
      icon?: IconSlug
    }
    settings: {
      [key: string]: unknown
      order: number | null
      isHidden: boolean
      custom?: boolean
      ai?: boolean
      show?: {
        variables: Record<string, { source: string; key: string }>
        condition: Record<string, unknown>
      }
    }
    step: Omit<Step, 'questions'>
    questionType: QuestionType
    questionTypeId: number
  }

  export type StepTypeDefaultQuestion = {
    id: number
    slug: string
    data: Record<string, unknown>
    order: number
    questionType: QuestionType
  }

  export type StepType = {
    id: number
    icon: IconSlug
    name: string
    order: number
    settings: Record<string, unknown>
    slug: string
    defaultQuestions: StepTypeDefaultQuestion[]
  }

  export type Step = {
    id: number
    questions: Question[]
    stepType: StepType
    stepTypeId: number
    settings: {
      [key: string]: unknown
      isHidden: boolean
      fake?: boolean
      summary?: {
        variables: Record<string, { source: string; key: string }>
        items: { condition: Record<string, unknown>; then: string; else?: string }[]
      }
    }
  }

  export type AIAnswerFeedback = {
    type: string
    reason: string | null
  }
}

export type SaveGuidedAttendanceForm = Pick<GuidedAttendanceForm, 'isArchived' | 'isPublished' | 'name'> & {
  id?: number
  diseaseId: number
  steps?: SaveGuidedAttendanceForm.Step[]
}
export namespace SaveGuidedAttendanceForm {
  export type Question = Pick<
    GuidedAttendanceForm.Question,
    'data' | 'questionType' | 'questionTypeId' | 'slug' | 'settings'
  > & {
    key: string
    id?: number
  }
  export type Step = Pick<GuidedAttendanceForm.Step, 'stepType' | 'stepTypeId' | 'settings'> & {
    id?: number
    questions: Question[]
  }
}

export function attendanceFormEndpoints(apiClient: AxiosInstance) {
  return {
    async getById(id: number): Promise<GuidedAttendanceForm> {
      return apiClient.get(`guided-attendance-form/${id}`)
    },

    async list({ filter, page, search, sort, withDeleted }: ListInput): Promise<ListOutput<GuidedAttendanceForm>> {
      return apiClient.get('guided-attendance-form', {
        params: {
          filter: filter ? JSON.stringify(filter) : undefined,
          page: page ? JSON.stringify(page) : undefined,
          search,
          sort: sort ? JSON.stringify(sort) : undefined,
          withDeleted,
        },
      })
    },

    async listStepTypesWithDefaultQuestions(): Promise<GuidedAttendanceForm.StepType[]> {
      return apiClient.get(`guided-attendance-form/step-type`)
    },

    async listFactoryQuestionTypes(): Promise<GuidedAttendanceForm.QuestionType[]> {
      return apiClient.get('guided-attendance-form/question-type/list-factory')
    },

    async save(form: SaveGuidedAttendanceForm): Promise<void> {
      if (form.id) {
        return apiClient.put(`guided-attendance-form/${form.id}`, form)
      }

      return apiClient.post(`guided-attendance-form`, form)
    },

    async edit(id: number, form: SaveGuidedAttendanceForm): Promise<void> {
      return apiClient.put(`guided-attendance-form/${id}`, form)
    },
    async publish(id: number): Promise<void> {
      return apiClient.put(`guided-attendance-form/${id}/publish`)
    },
    async toggleArchive(id: number): Promise<void> {
      return apiClient.patch(`guided-attendance-form/${id}/archive`)
    },
    async upsertAIAnswerFeedback(
      attendanceId: number,
      formId: number,
      questionSlug: string,
      data: GuidedAttendanceForm.AIAnswerFeedback,
    ) {
      return apiClient.put(
        `save-ai-generated-answer-opinion/${attendanceId}/form/${formId}/question/${questionSlug}`,
        data,
      )
    },
    async generateAIMessageByQuestion(
      attendanceId: number,
      questionId: number,
      force: boolean = false,
    ): Promise<unknown> {
      return apiClient.put(
        `generate-attendance-ai-message/${attendanceId}/question/${questionId}`,
        {},
        {
          params: {
            force,
          },
        },
      )
    },
  }
}
